import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Pagination from './Pagination';

/**
 * Admin Revenue Dashboard - Shows ClickPay fee revenue per merchant and total.
 */
const AdminRevenueDashboard = () => {
  const [activeView, setActiveView] = useState('total'); // 'total' or 'merchant'
  const [merchantId, setMerchantId] = useState('');
  const [totalData, setTotalData] = useState(null);
  const [merchantData, setMerchantData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [revenuePage, setRevenuePage] = useState(1);
  const [txPage, setTxPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    fetchTotalRevenue();
  }, []);

  const fetchTotalRevenue = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/admin/revenue/total');
      if (res.data.success) {
        setTotalData(res.data);
      } else {
        setError(res.data.message || 'Failed to load');
      }
    } catch (e) {
      setError('Failed to fetch revenue: ' + (e.response?.data?.message || e.message));
    } finally { setLoading(false); }
  };

  const fetchMerchantRevenue = async () => {
    if (!merchantId || merchantId === '0') {
      setError('❌ Please enter a valid Merchant ID');
      setMerchantData(null);
      return;
    }
    setLoading(true);
    setError('');
    setMerchantData(null);
    setTxPage(1);
    try {
      // Verify merchant exists first
      try {
        const merchantRes = await axios.get(`/api/admin/merchants/${merchantId}`);
        if (!merchantRes.data.success || !merchantRes.data.data) {
          setError(`❌ Merchant #${merchantId} does not exist`);
          setLoading(false);
          return;
        }
      } catch (merr) {
        setError(`❌ Merchant #${merchantId} does not exist`);
        setLoading(false);
        return;
      }

      const res = await axios.get(`/api/admin/revenue/merchant/${merchantId}`);
      if (res.data.success) {
        setMerchantData(res.data);
      } else {
        setError(res.data.message || 'Failed to load');
      }
    } catch (e) {
      setError('❌ Failed to fetch revenue: ' + (e.response?.data?.message || e.message));
    } finally { setLoading(false); }
  };

  const fmt = (val) => {
    if (val === null || val === undefined) return '₹0.00';
    return '₹' + Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div style={{ padding: '20px' }}>
      <h3 style={{ marginBottom: '8px' }}>📊 ClickPay Revenue Dashboard</h3>
      <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>
        Revenue calculated from configured fee slabs applied to successful transactions.
      </p>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button onClick={() => { setActiveView('total'); fetchTotalRevenue(); }} style={tabStyle(activeView === 'total')}>
          💰 Total Revenue (All Merchants)
        </button>
        <button onClick={() => setActiveView('merchant')} style={tabStyle(activeView === 'merchant')}>
          🏪 Per Merchant Breakdown
        </button>
      </div>

      {error && <div style={{ padding: '8px 12px', background: '#ffebee', color: '#c62828', borderRadius: '4px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}

      {/* Total Revenue View */}
      {activeView === 'total' && totalData && (
        <div>
          {/* Grand Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' }}>
            <SummaryCard label="Total Merchants" value={totalData.summary.totalMerchants} color="#1976d2" />
            <SummaryCard label="Successful Txns" value={totalData.summary.totalSuccessfulTransactions} color="#388e3c" />
            <SummaryCard label="Transaction Volume" value={fmt(totalData.summary.totalTransactionVolume)} color="#6a1b9a" />
            <SummaryCard label="ClickPay Fee" value={fmt(totalData.summary.totalClickPayFee)} color="#e65100" />
            <SummaryCard label="GST Collected" value={fmt(totalData.summary.totalGst)} color="#00695c" />
            <SummaryCard label="Total Revenue" value={fmt(totalData.summary.totalRevenue)} color="#b71c1c" isBig />
          </div>

          {/* Per Merchant Table */}
          <h4 style={{ marginBottom: '8px' }}>Revenue by Merchant</h4>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={thStyle}>Merchant ID</th>
                <th style={thStyle}>Successful Txns</th>
                <th style={thStyle}>Volume</th>
                <th style={thStyle}>ClickPay Fee</th>
                <th style={thStyle}>GST</th>
                <th style={thStyle}>Total Revenue</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {totalData.merchants && totalData.merchants
                .slice((revenuePage - 1) * PAGE_SIZE, revenuePage * PAGE_SIZE)
                .map((m, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={tdStyle}>{m.merchantId}</td>
                  <td style={tdStyle}>{m.successfulTransactions}</td>
                  <td style={tdStyle}>{fmt(m.totalVolume)}</td>
                  <td style={tdStyle}>{fmt(m.totalFee)}</td>
                  <td style={tdStyle}>{fmt(m.totalGst)}</td>
                  <td style={{ ...tdStyle, fontWeight: '600', color: '#b71c1c' }}>{fmt(m.totalRevenue)}</td>
                  <td style={tdStyle}>
                    <button onClick={() => { setMerchantId(String(m.merchantId)); setActiveView('merchant'); setTimeout(() => fetchMerchantRevenue(), 100); }} style={linkBtn}>View Details</button>
                  </td>
                </tr>
              ))}
              {(!totalData.merchants || totalData.merchants.length === 0) && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '16px', color: '#888' }}>No revenue data yet</td></tr>
              )}
            </tbody>
          </table>
          {totalData.merchants && totalData.merchants.length > PAGE_SIZE && (
            <Pagination
              currentPage={revenuePage}
              totalPages={Math.ceil(totalData.merchants.length / PAGE_SIZE)}
              goToPage={setRevenuePage}
              total={totalData.merchants.length}
              pageSize={PAGE_SIZE}
            />
          )}
        </div>
      )}

      {/* Per Merchant View */}
      {activeView === 'merchant' && (
        <div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
            <label style={{ fontWeight: '600' }}>Merchant ID:</label>
            <input type="number" value={merchantId} onChange={e => { const val = e.target.value; if (val === '' || /^\d+$/.test(val)) setMerchantId(val); }} placeholder="Enter merchant ID" min="1" style={inputStyle} />
            <button onClick={fetchMerchantRevenue} disabled={!merchantId || loading} style={btnStyle('#0d6efd')}>Load Revenue</button>
          </div>

          {merchantData && (
            <>
              {/* Merchant Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                <SummaryCard label="Transactions" value={merchantData.summary.totalTransactions} color="#1976d2" />
                <SummaryCard label="Total Amount" value={fmt(merchantData.summary.totalTransactionAmount)} color="#6a1b9a" />
                <SummaryCard label="ClickPay Fee" value={fmt(merchantData.summary.totalClickPayFee)} color="#e65100" />
                <SummaryCard label="GST" value={fmt(merchantData.summary.totalGst)} color="#00695c" />
                <SummaryCard label="Total Revenue" value={fmt(merchantData.summary.totalRevenue)} color="#b71c1c" isBig />
                <SummaryCard label="Merchant Net" value={fmt(merchantData.summary.totalMerchantNet)} color="#1565c0" />
              </div>

              {/* Transaction-level breakdown */}
              <h4 style={{ marginBottom: '8px' }}>Transaction Breakdown (Merchant #{merchantData.summary.merchantId})</h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={thStyle}>Transaction ID</th>
                      <th style={thStyle}>Customer</th>
                      <th style={thStyle}>Description</th>
                      <th style={thStyle}>Amount</th>
                      <th style={thStyle}>Fee Flat</th>
                      <th style={thStyle}>Fee %</th>
                      <th style={thStyle}>ClickPay Fee</th>
                      <th style={thStyle}>GST</th>
                      <th style={thStyle}>Total Charge</th>
                      <th style={thStyle}>Merchant Net</th>
                      <th style={thStyle}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {merchantData.transactions && merchantData.transactions
                      .slice((txPage - 1) * PAGE_SIZE, txPage * PAGE_SIZE)
                      .map((t, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ ...tdStyle, fontSize: '11px', fontFamily: 'monospace' }}>{t.transactionId}</td>
                        <td style={tdStyle}>{t.customerName || '-'}</td>
                        <td style={tdStyle}>{t.description || '-'}</td>
                        <td style={tdStyle}>{fmt(t.amount)}</td>
                        <td style={tdStyle}>{fmt(t.feeFlat)}</td>
                        <td style={tdStyle}>{t.feePercent}%</td>
                        <td style={{ ...tdStyle, color: '#e65100', fontWeight: '500' }}>{fmt(t.clickPayFee)}</td>
                        <td style={tdStyle}>{fmt(t.gst)}</td>
                        <td style={{ ...tdStyle, color: '#b71c1c', fontWeight: '600' }}>{fmt(t.totalCharge)}</td>
                        <td style={{ ...tdStyle, color: '#1565c0' }}>{fmt(t.merchantNet)}</td>
                        <td style={{ ...tdStyle, fontSize: '11px' }}>{t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-IN') : '-'}</td>
                      </tr>
                    ))}
                    {(!merchantData.transactions || merchantData.transactions.length === 0) && (
                      <tr><td colSpan={11} style={{ textAlign: 'center', padding: '16px', color: '#888' }}>No successful transactions found</td></tr>
                    )}
                  </tbody>
                </table>
                {merchantData.transactions && merchantData.transactions.length > PAGE_SIZE && (
                  <Pagination
                    currentPage={txPage}
                    totalPages={Math.ceil(merchantData.transactions.length / PAGE_SIZE)}
                    goToPage={setTxPage}
                    total={merchantData.transactions.length}
                    pageSize={PAGE_SIZE}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>Loading...</div>}
    </div>
  );
};

const SummaryCard = ({ label, value, color, isBig }) => (
  <div style={{ padding: '12px 16px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px', borderLeft: `4px solid ${color}` }}>
    <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: isBig ? '18px' : '15px', fontWeight: '700', color }}>{value}</div>
  </div>
);

const tabStyle = (active) => ({
  padding: '8px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  background: active ? '#1976d2' : '#e0e0e0', color: active ? '#fff' : '#333'
});
const btnStyle = (bg) => ({ padding: '8px 16px', background: bg, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' });
const linkBtn = { background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' };
const inputStyle = { padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', width: '120px' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '12px' };
const thStyle = { padding: '8px 6px', textAlign: 'left', borderBottom: '2px solid #ddd', fontSize: '11px', fontWeight: '600' };
const tdStyle = { padding: '6px', fontSize: '12px' };

export default AdminRevenueDashboard;
